import { withNamespaces } from "react-i18next";
import { Button, Col, Container, Row, Spinner } from "reactstrap";

import Navigator from "src/components/Navigator";
import { EMPTY_VALUE, FUNCTION_STATUS } from "src/modules/data-protection/pages/admin/administration/user-management/tabs-contents/functions/components/functions-table/helpers";

import AssignedUsers from "./components/assigned-users";
import DeleteFunctionModal from "./components/delete-function-modal";
import FunctionInformation from "./components/function-information";
import useFunctionDetailsPage from "./hooks/useFunctionDetailsPage";

import "./index.scss";

const FunctionDetails = ({ t }) => {
    const {
        functionQuery,
        organizationFunction,
        title,
        setTitle,
        description,
        setDescription,
        titleError,
        setTitleError,
        pendingDpoUser,
        setPendingDpoUser,
        isDeleteOpen,
        setIsDeleteOpen,
        isReserved,
        handleCancel,
        handleSave,
        isSaving,
        mutations,
        functionsListPath,
        usersListPath,
    } = useFunctionDetailsPage(t);

    const status = FUNCTION_STATUS[organizationFunction?.status];
    const pageTitle = organizationFunction?.title || "";
    const subtitle = organizationFunction?.description
        ? organizationFunction.description
        : EMPTY_VALUE;

    if (functionQuery.isLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                </Container>
            </div>
        );
    }

    if (!organizationFunction) {
        return null;
    }

    return (
        <div className="page-content function-details-page">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle={
                                <span className="d-block">
                                    <span className="d-inline-flex align-items-center gap-2">
                                        {pageTitle}
                                        {status ? (
                                            <Button color={status.color} size="sm">
                                                {t(status.label)}
                                            </Button>
                                        ) : null}
                                    </span>
                                    <span className="d-block text-muted text-truncate fw-normal font-size-14 function-details-header-subtitle">
                                        {subtitle}
                                    </span>
                                </span>
                            }
                            breadCrumbs={[
                                { title: t("SIP"), link: "/" },
                                {
                                    title: t("User Management"),
                                    link: usersListPath,
                                },
                                {
                                    title: t("Functions"),
                                    link: functionsListPath,
                                },
                                { title: pageTitle, link: "#" },
                            ]}
                            backButtonOptions={(defaults) => ({
                                ...defaults,
                                enable: true,
                                onClick: handleCancel,
                            })}
                            translate={false}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col xl="12">
                        <FunctionInformation
                            organizationFunction={organizationFunction}
                            title={title}
                            description={description}
                            titleError={titleError}
                            onTitleChange={(value) => {
                                setTitle(value);
                                if (titleError && value.trim()) {
                                    setTitleError(false);
                                }
                            }}
                            onDescriptionChange={setDescription}
                            disabled={isSaving}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col xl="12">
                        <AssignedUsers
                            organizationFunction={organizationFunction}
                            pendingDpoUser={pendingDpoUser}
                            onPendingDpoUserChange={setPendingDpoUser}
                        />
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col
                        sm="12"
                        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                    >
                        <div>
                            {!isReserved ? (
                                <Button
                                    color="danger"
                                    type="button"
                                    onClick={() => setIsDeleteOpen(true)}
                                >
                                    {t("Delete Function")}
                                </Button>
                            ) : null}
                        </div>
                        <div>
                            <Button
                                color="secondary"
                                className="me-2"
                                type="button"
                                disabled={isSaving}
                                onClick={handleCancel}
                            >
                                {t("Cancel")}
                            </Button>
                            <Button
                                color="primary"
                                type="button"
                                disabled={isSaving}
                                onClick={handleSave}
                            >
                                {isSaving ? (
                                    <Spinner className="me-2" size="sm" />
                                ) : null}
                                {t("Save Changes")}
                            </Button>
                        </div>
                    </Col>
                </Row>

                <DeleteFunctionModal
                    isOpen={isDeleteOpen}
                    toggle={() => setIsDeleteOpen(false)}
                    organizationFunction={organizationFunction}
                    isDeleting={mutations.removeMutation.isLoading}
                    onConfirm={() => mutations.removeMutation.mutate()}
                />
            </Container>
        </div>
    );
};

export default withNamespaces()(FunctionDetails);
